import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); nav("/"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const quick = (em) => { setEmail(em); setPassword("PetroFlow@2026"); };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative bg-[#12346B] flex-col justify-between p-12 text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1598408745613-178751e2ccde?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="refinery" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/prime-logo.jpg" alt="Prime Contractors, Inc." className="h-14 w-14 rounded-full object-cover bg-white ring-2 ring-white/40" />
          <div>
            <div className="font-head font-extrabold text-xl">PCI PetroFlow ERP™</div>
            <div className="text-xs text-slate-300 uppercase tracking-widest">Petroleum Trading System of Record</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-head text-4xl font-bold leading-tight">Control the complete flow of petroleum, money & evidence.</h1>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">
            Procure → Contract → Pay → Transfer → Store → Control → Sell → Collect → Authorize → Deliver → Reconcile → Close → Report.
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-4 w-4" /> Segregation of duties · Full audit trail · Configurable controls
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-400">Prime Contractors, Inc. · Technology Partner: Smithsonian Group Inc.</div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-slate-50 p-6 relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-center bg-no-repeat bg-contain opacity-[0.04]" style={{ backgroundImage: "url('/prime-logo.jpg')" }} />
        <div className="w-full max-w-md relative z-10">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/prime-logo.jpg" alt="Prime Contractors, Inc." className="h-10 w-10 rounded-full object-cover" />
            <span className="font-head font-extrabold text-lg text-slate-900">PCI PetroFlow ERP™</span>
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access the petroleum trading control platform.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-slate-600">Work Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@pci.com" required />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-slate-600">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full bg-[#12346B] hover:bg-[#12346B]/90">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Sign in
            </Button>
          </form>

          <p className="mt-4 text-[11px] text-slate-400 text-center">Accounts are provisioned by your administrator. Contact your admin for access.</p>

          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Demo accounts (password: PetroFlow@2026)</div>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              {[["sgincliberia@gmail.com", "Admin"], ["procurement@pci.com", "Procurement"], ["finance@pci.com", "Finance"], ["executive@pci.com", "Executive"], ["sales@pci.com", "Sales"], ["logistics@pci.com", "Logistics"]].map(([em, role]) => (
                <button key={em} type="button" onClick={() => quick(em)} data-testid={`demo-${role.toLowerCase()}`}
                  className="text-left px-2 py-1.5 rounded border border-slate-100 hover:bg-slate-50">
                  <div className="font-medium text-slate-700">{role}</div>
                  <div className="text-slate-400 truncate">{em}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
